// Utilitários analíticos da página de detalhe do envio: formatação
// de durações (deltas da timeline), de tamanho de payload e
// tokenização de JSON para o syntax highlight leve (sem lib nova).

// Duração legível a partir de milissegundos: "584ms", "1,5s",
// "2min 5s", "1h 12min" — decimal no locale do usuário.
export const formatNotificationDuration = (
  durationMs: number,
  language: string
): string => {
  const ms = Math.max(0, Math.round(durationMs))
  if (ms < 1_000) return `${ms}ms`
  const numberFormat = new Intl.NumberFormat(language, {
    maximumFractionDigits: 1,
  })
  if (ms < 60_000) {
    return `${numberFormat.format(ms / 1_000)}s`
  }
  if (ms < 3_600_000) {
    const minutes = Math.floor(ms / 60_000)
    const seconds = Math.round((ms % 60_000) / 1_000)
    return seconds > 0
      ? `${minutes}min ${seconds}s`
      : `${minutes}min`
  }
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.round((ms % 3_600_000) / 60_000)
  return minutes > 0
    ? `${hours}h ${minutes}min`
    : `${hours}h`
}

// Tamanho legível a partir de bytes: "812 B", "2,1 KB", "1,4 MB".
export const formatNotificationBytes = (
  sizeBytes: number,
  language: string
): string => {
  const numberFormat = new Intl.NumberFormat(language, {
    maximumFractionDigits: 1,
  })
  if (sizeBytes < 1_024) return `${sizeBytes} B`
  if (sizeBytes < 1_048_576) {
    return `${numberFormat.format(sizeBytes / 1_024)} KB`
  }
  return `${numberFormat.format(sizeBytes / 1_048_576)} MB`
}

// ── Syntax highlight leve de JSON (tokenização por regex) ──────────
export type NotificationJsonTokenType =
  | "key"
  | "string"
  | "number"
  | "boolean"
  | "null"
  | "punctuation"

export type NotificationJsonToken = {
  type: NotificationJsonTokenType
  text: string
}

// String JSON (com escapes) opcionalmente seguida de ":" (= chave),
// número, boolean ou null; o resto (pontuação/indentação) fica
// entre os matches.
const JSON_TOKEN_REGEX =
  /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false)\b|\bnull\b/g

// Tokeniza a saída de JSON.stringify(value, null, 2) para renderizar
// com classes por tipo — chave, string, número, boolean e null.
export const tokenizeJsonForHighlight = (
  json: string
): NotificationJsonToken[] => {
  const tokens: NotificationJsonToken[] = []
  let lastIndex = 0
  for (const match of json.matchAll(JSON_TOKEN_REGEX)) {
    const index = match.index ?? 0
    if (index > lastIndex) {
      tokens.push({
        type: "punctuation",
        text: json.slice(lastIndex, index),
      })
    }
    const [
      fullMatch,
      stringToken,
      keyColon,
      numberToken,
      booleanToken,
    ] = match
    if (stringToken !== undefined) {
      if (keyColon !== undefined) {
        tokens.push({ type: "key", text: stringToken })
        tokens.push({
          type: "punctuation",
          text: keyColon,
        })
      } else {
        tokens.push({
          type: "string",
          text: stringToken,
        })
      }
    } else if (numberToken !== undefined) {
      tokens.push({ type: "number", text: numberToken })
    } else if (booleanToken !== undefined) {
      tokens.push({ type: "boolean", text: booleanToken })
    } else {
      tokens.push({ type: "null", text: fullMatch })
    }
    lastIndex = index + fullMatch.length
  }
  if (lastIndex < json.length) {
    tokens.push({
      type: "punctuation",
      text: json.slice(lastIndex),
    })
  }
  return tokens
}
